import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Toast Primitive - Notification toast component
 *
 * Displays brief notifications to users with auto-dismiss functionality.
 * Supports different variants (success, error, warning, info).
 *
 * @see docs/architecture/OwnYou_architecture_v13.md Section 4.4
 */
import { useEffect, useState, useCallback } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../utils/cn';
const toastVariants = cva('flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg', {
    variants: {
        variant: {
            default: 'bg-white text-neutral-900 border border-neutral-200',
            success: 'bg-success text-white',
            error: 'bg-error text-white',
            warning: 'bg-warning text-neutral-900',
            info: 'bg-info text-white',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});
/**
 * Toast notification component
 *
 * @example
 * ```tsx
 * <Toast
 *   variant="success"
 *   message="Settings saved successfully!"
 *   duration={3000}
 *   onDismiss={() => setShowToast(false)}
 * />
 * ```
 */
export function Toast({ className, variant, message, title, duration = 5000, onDismiss, open = true, showClose = true, ...props }) {
    const [isVisible, setIsVisible] = useState(open);
    const [isLeaving, setIsLeaving] = useState(false);
    const handleDismiss = useCallback(() => {
        setIsLeaving(true);
        setTimeout(() => {
            setIsVisible(false);
            onDismiss?.();
        }, 200); // Match animation duration
    }, [onDismiss]);
    // Auto-dismiss after duration
    useEffect(() => {
        if (duration > 0 && isVisible) {
            const timer = setTimeout(handleDismiss, duration);
            return () => clearTimeout(timer);
        }
    }, [duration, isVisible, handleDismiss]);
    // Sync with open prop
    useEffect(() => {
        if (open) {
            setIsVisible(true);
            setIsLeaving(false);
        }
        else {
            handleDismiss();
        }
    }, [open, handleDismiss]);
    if (!isVisible) {
        return null;
    }
    return (_jsxs("div", { className: cn(toastVariants({ variant }), isLeaving ? 'animate-out fade-out-0 slide-out-to-right-2' : 'animate-in fade-in-0 slide-in-from-right-2', className), role: "alert", "aria-live": "polite", ...props, children: [_jsx(ToastIcon, { variant: variant }), _jsxs("div", { className: "flex-1", children: [title && (_jsx("div", { className: "text-body font-semibold", children: title })), _jsx("div", { className: "text-caption", children: message })] }), showClose && (_jsx("button", { type: "button", onClick: handleDismiss, className: "rounded-full p-1 hover:bg-black/10 focus:outline-none", "aria-label": "Dismiss notification", children: _jsx("svg", { className: "h-4 w-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) }))] }));
}
/**
 * Toast icon based on variant
 */
function ToastIcon({ variant }) {
    const iconClass = 'h-5 w-5 flex-shrink-0';
    switch (variant) {
        case 'success':
            return (_jsx("svg", { className: iconClass, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 13l4 4L19 7" }) }));
        case 'error':
            return (_jsx("svg", { className: iconClass, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }));
        case 'warning':
            return (_jsx("svg", { className: iconClass, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" }) }));
        case 'info':
        default:
            return (_jsx("svg", { className: iconClass, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" }) }));
    }
}
export function ToastContainer({ className, position = 'bottom-right', children, ...props }) {
    const positionClasses = {
        'top-right': 'top-4 right-4',
        'top-left': 'top-4 left-4',
        'bottom-right': 'bottom-4 right-4',
        'bottom-left': 'bottom-4 left-4',
        'top-center': 'top-4 left-1/2 -translate-x-1/2',
        'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
    };
    return (_jsx("div", { className: cn('fixed z-50 flex flex-col gap-2', positionClasses[position], className), ...props, children: children }));
}
export { toastVariants };
//# sourceMappingURL=Toast.js.map